import { valibotResolver } from "@hookform/resolvers/valibot";
import { ArrowCounterClockwise, Warning } from "@phosphor-icons/react";
import { useForm } from "react-hook-form";

import RenderIf from "common/components/RenderIf";
import Alert from "common/components/ui/Alert";
import { Button } from "common/components/ui/Button";
import { DialogDescription, DialogTitle } from "common/components/ui/Dialog";
import { DrawerDescription, DrawerTitle } from "common/components/ui/Drawer";
import FormField from "common/components/ui/FormField";
import { Input } from "common/components/ui/Input";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import usePromotionsMutations from "common/datahooks/usePromotionsMutations";
import { HTTPError } from "common/helpers/HTTP";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import { AssignSeatsSchema, type AssignSeatsSchemaType } from "common/schemas";
import type { AppSumoPromotion, LTDPromotion } from "common/types";

function SeatsCount({ text, count }: { text: string; count: number }) {
  return (
    <div className="mt-4 flex items-center justify-between text-black-700">
      <span>{text}</span>
      <span className="text-sm-bold">{count}</span>
    </div>
  );
}

interface WorkspacePromotionProps {
  promotion: AppSumoPromotion | LTDPromotion;
  onClose: () => void;
  onReset?: () => void;
  closeText?: string;
}

export default function Promotion({
  promotion,
  onClose,
  onReset = null,
  closeText = "Close",
}: WorkspacePromotionProps) {
  const isDesktop = useTwBreakpoint("lg");
  const {
    id: workspaceId,
    name,
    lifetime_license_count: lifetimeLicenseCount,
  } = useSelectedWorkspaceContext();

  const {
    addLifetimeLicenses,
    isAddingLifetimeLicenses,
    addLifetimeLicensesError,
  } = usePromotionsMutations();

  const { type, unassigned_license_count: seatsRemaining } = promotion;

  const canAssign =
    !!seatsRemaining &&
    (type !== "appsumo" || promotion.unassigned_license_count > 0);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm({
    defaultValues: {
      seats: 1,
    },
    resolver: valibotResolver(AssignSeatsSchema),
  });

  function onAssign(data: AssignSeatsSchemaType) {
    if (data.seats > seatsRemaining) {
      setError("seats", { message: "You don't have that many licenses" });
    } else {
      addLifetimeLicenses({
        count: data.seats,
        source: type,
        workspaceId,
      }).then(onClose);
    }
  }

  const Title = isDesktop ? DialogTitle : DrawerTitle;
  const Description = isDesktop ? DialogDescription : DrawerDescription;

  return (
    <>
      <Title>Manage your lifetime seats</Title>
      {type === "appsumo" && (
        <Description className="mb-4">
          Workspaces remaining: {promotion.unassigned_workspaces}
        </Description>
      )}

      <RenderIf condition={!!onReset}>
        <Button
          className="mb-4 ml-auto mt-8"
          variant="outline-black"
          leftIcon={<ArrowCounterClockwise />}
          onClick={onReset}
          size={isDesktop ? "sm" : "md"}
        >
          Reset seats and workspaces
        </Button>
      </RenderIf>

      <FormField label="Workspace" htmlFor="workspace-select">
        <Input disabled value={name} />
      </FormField>

      <SeatsCount
        text="Lifetime seats on this workspace"
        count={lifetimeLicenseCount ?? 0}
      />

      <FormField
        label="Seats to add"
        htmlFor="licenses"
        className="mt-8"
        error={errors.seats?.message}
      >
        <Input
          name="licenses"
          type="number"
          {...register("seats", {
            valueAsNumber: true,
          })}
        />
      </FormField>

      <SeatsCount text="Total seats remaining" count={seatsRemaining} />

      <Alert className="mt-4" variant="warning">
        <span className="text-xs-bold">
          {canAssign ? "Add carefully" : "No seats available"}.
        </span>{" "}
        {canAssign
          ? "Adding seats is a one-way street. If you want to re-allocate seats, you will have to reset and start from scratch"
          : "You have used up all of your lifetime seats. You can upgrade your package if your promotion is ongoing, or re-assign seats by resetting"}
      </Alert>

      {addLifetimeLicensesError instanceof HTTPError &&
        addLifetimeLicensesError.type === "InvalidSubscriptionStatus" && (
          <div className="flex items-center gap-0.5 text-xs text-yellow-500 mt-4">
            <Warning weight="fill" size={16} />
            You cannot apply lifetime seats to a workspace in trial. Choose a
            different workspace, or cancel your trial
          </div>
        )}

      <div className="mt-8 flex justify-between gap-x-2.5">
        <Button
          variant="secondary-black"
          className="lg:flex-1"
          disabled={isAddingLifetimeLicenses}
          onClick={onClose}
        >
          {closeText}
        </Button>

        <Button
          onClick={handleSubmit(onAssign)}
          className="lg:flex-1"
          isLoading={isAddingLifetimeLicenses}
          disabled={!canAssign}
        >
          Assign
        </Button>
      </div>
    </>
  );
}
